import Utils from "models/helper/Utils";

const PROFILE_TYPES = Utils.getInstance().getProfileTypes();

const SIGNUP_INTENTS = Object.freeze({
    LISTENER: "listener",
    SHORTCAST_CREATOR: "shortcast_creator",
    JOURNEY_CREATOR: "journey_creator",
    CREATOR_WITH_TREBBLE_SETUP: "creator_with_trebble_setup",
    TEMPORARY_VISITOR: "temporary_visitor",
});

const MOBILE_BUILT_IN_ACCESS_CODE = "7B2-DXWC-EMP1";
const WEB_BUILT_IN_ACCESS_CODE = "WEBB-58D2-V8V";
const WEB_LEGACY_BUILT_IN_ACCESS_CODE = "WEBB-58D2-V8VT";

const trimString = function(value) {
    return typeof value === "string" ? value.trim() : "";
};

const getKnownBuiltInAccessCodes = function() {
    return {
        [MOBILE_BUILT_IN_ACCESS_CODE]: true,
        [WEB_BUILT_IN_ACCESS_CODE]: true,
        [WEB_LEGACY_BUILT_IN_ACCESS_CODE]: true,
    };
};

const getCurrentAccessCodeEnvironment = function() {
    if (typeof window === "undefined") {
        return "web";
    }
    return window.waitForCordovaToLoad ? "mobile" : "web";
};

const isListenerOnlySignupSurface = function() {
    if (typeof window === "undefined") {
        return false;
    }
    const docBody = typeof document !== "undefined" && document.body ? document.body : null;
    const bodyClassList = docBody && docBody.classList ? docBody.classList : null;
    return !!(
        window.waitForCordovaToLoad ||
        (bodyClassList && bodyClassList.contains("embedded")) ||
        (window.trebble && window.trebble.entryPointUrlHashLoadedPlayerQueue) ||
        (window.trebble && window.trebble.isEmbededLoginInPage) ||
        (window.trebble && window.trebble.allowEasySignupForListenersAtLogin)
    );
};

const getBuiltInListenerAccessCode = function() {
    return getCurrentAccessCodeEnvironment() === "mobile" ? MOBILE_BUILT_IN_ACCESS_CODE : WEB_BUILT_IN_ACCESS_CODE;
};

const getBuiltInCreatorAccessCode = function() {
    return getCurrentAccessCodeEnvironment() === "mobile" ? MOBILE_BUILT_IN_ACCESS_CODE : WEB_BUILT_IN_ACCESS_CODE;
};

const getNormalizedSignupIntent = function(value) {
    const normalizedValue = trimString(value);
    if (!normalizedValue) {
        return null;
    }

    switch (normalizedValue) {
        case SIGNUP_INTENTS.LISTENER:
        case SIGNUP_INTENTS.SHORTCAST_CREATOR:
        case SIGNUP_INTENTS.JOURNEY_CREATOR:
        case SIGNUP_INTENTS.CREATOR_WITH_TREBBLE_SETUP:
        case SIGNUP_INTENTS.TEMPORARY_VISITOR:
            return normalizedValue;
        default:
            return null;
    }
};

const getSignupIntentFromProfileType = function(profileType, hasCreatorSetupMetadata) {
    if (hasCreatorSetupMetadata) {
        return SIGNUP_INTENTS.CREATOR_WITH_TREBBLE_SETUP;
    }

    switch (profileType) {
        case PROFILE_TYPES.LISTENER:
            return SIGNUP_INTENTS.LISTENER;
        case PROFILE_TYPES.JOURNEY_CREATOR:
            return SIGNUP_INTENTS.JOURNEY_CREATOR;
        case PROFILE_TYPES.SHORTCAST_CREATOR:
            return SIGNUP_INTENTS.SHORTCAST_CREATOR;
        default:
            return null;
    }
};

const getDefaultAccessCodeForSignupIntent = function(signupIntent) {
    switch (signupIntent) {
        case SIGNUP_INTENTS.LISTENER:
            return getBuiltInListenerAccessCode();
        case SIGNUP_INTENTS.JOURNEY_CREATOR:
        case SIGNUP_INTENTS.SHORTCAST_CREATOR:
        case SIGNUP_INTENTS.CREATOR_WITH_TREBBLE_SETUP:
            return getBuiltInCreatorAccessCode();
        default:
            return null;
    }
};

const hasExplicitCustomAccessCode = function(accessCode) {
    const trimmedAccessCode = trimString(accessCode);
    return !!(trimmedAccessCode && !getKnownBuiltInAccessCodes()[trimmedAccessCode]);
};

const getSignupIntentFromPayload = function(payload, options) {
    const resolvedPayload = payload || {};
    const resolvedOptions = options || {};
    const context = resolvedPayload.context && typeof resolvedPayload.context === "object" ? resolvedPayload.context : {};
    const explicitSignupIntent =
        getNormalizedSignupIntent(resolvedOptions.signupIntent) ||
        getNormalizedSignupIntent(resolvedPayload.signupIntent) ||
        getNormalizedSignupIntent(context.signupIntent);

    if (explicitSignupIntent) {
        return explicitSignupIntent;
    }

    if (resolvedPayload.temporary === true || resolvedPayload.visitor === true) {
        return SIGNUP_INTENTS.TEMPORARY_VISITOR;
    }

    const resolvedProfileType = resolvedPayload.profileType || null;
    const hasCreatorSetupMetadata = !!trimString(resolvedPayload.trebbleName);
    const profileTypeDerivedIntent = getSignupIntentFromProfileType(resolvedProfileType, hasCreatorSetupMetadata);
    if (profileTypeDerivedIntent) {
        return profileTypeDerivedIntent;
    }

    if (hasExplicitCustomAccessCode(resolvedPayload.accessCode)) {
        return null;
    }

    return getNormalizedSignupIntent(resolvedOptions.defaultSignupIntent);
};

const getDefaultSignupIntentForCurrentSurface = function() {
    if (isListenerOnlySignupSurface()) {
        return SIGNUP_INTENTS.LISTENER;
    }

    return SIGNUP_INTENTS.SHORTCAST_CREATOR;
};

const normalizeSignupPayload = function(payload, options) {
    const resolvedOptions = options || {};
    const normalizedPayload = Object.assign({}, payload || {});
    const normalizedContext =
        normalizedPayload.context && typeof normalizedPayload.context === "object" ?
            Object.assign({}, normalizedPayload.context) :
            {};

    if (normalizedPayload.accessCode !== undefined && normalizedPayload.accessCode !== null) {
        const trimmedAccessCode = trimString(normalizedPayload.accessCode);
        normalizedPayload.accessCode = trimmedAccessCode || null;
    }

    const signupIntent = getSignupIntentFromPayload(normalizedPayload, Object.assign({}, resolvedOptions, {
        defaultSignupIntent: resolvedOptions.defaultSignupIntent || getDefaultSignupIntentForCurrentSurface(),
    }));

    if (!normalizedPayload.profileType && !trimString(normalizedPayload.trebbleName)) {
        if (signupIntent === SIGNUP_INTENTS.SHORTCAST_CREATOR) {
            normalizedPayload.profileType = PROFILE_TYPES.SHORTCAST_CREATOR;
        } else if (signupIntent === SIGNUP_INTENTS.JOURNEY_CREATOR) {
            normalizedPayload.profileType = PROFILE_TYPES.JOURNEY_CREATOR;
        } else if (signupIntent === SIGNUP_INTENTS.LISTENER) {
            normalizedPayload.profileType = PROFILE_TYPES.LISTENER;
        }
    }

    if (!normalizedPayload.accessCode) {
        const defaultAccessCode = getDefaultAccessCodeForSignupIntent(signupIntent);
        if (defaultAccessCode) {
            normalizedPayload.accessCode = defaultAccessCode;
        }
    }

    if (signupIntent) {
        normalizedPayload.signupIntent = signupIntent;
        normalizedContext.signupIntent = signupIntent;
    }

    if (Object.keys(normalizedContext).length > 0) {
        normalizedPayload.context = normalizedContext;
    }

    return normalizedPayload;
};

const getRoleSelectionAccessCode = function(currentAccessCode, signupIntent) {
    if (hasExplicitCustomAccessCode(currentAccessCode)) {
        return trimString(currentAccessCode);
    }

    return getDefaultAccessCodeForSignupIntent(signupIntent);
};

export default {
    SIGNUP_INTENTS,
    getBuiltInCreatorAccessCode,
    getBuiltInListenerAccessCode,
    getDefaultSignupIntentForCurrentSurface,
    getDefaultAccessCodeForSignupIntent,
    getNormalizedSignupIntent,
    getRoleSelectionAccessCode,
    hasExplicitCustomAccessCode,
    isListenerOnlySignupSurface,
    normalizeSignupPayload,
};
